import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../services/api';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { Ticket, CalendarDays, Users, ChevronDown, ChevronRight, CheckCircle, Clock, XCircle, RefreshCw, Activity, ExternalLink, Link2, Copy, Download, QrCode, Trash2, X, Mail, Phone } from 'lucide-react';

const TICKET_STATUS = {
  valid: { label: 'Valido', color: '#10B981' },
  checked_in: { label: 'Utilizzato', color: '#64748B' },
  pending: { label: 'In attesa', color: '#F59E0B' },
  cancelled: { label: 'Annullato', color: '#EF4444' },
};

export default function AdminEventsTickets() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [bookings, setBookings] = useState({});
  const [bookingsList, setBookingsList] = useState(null); // { event, data } when modal open
  const [resetting, setResetting] = useState(null); // event_id while reset in flight

  /**
   * Renders the on-screen QR (qrcode.react SVG) onto a 1024×1280 canvas
   * with the event name and date below, then downloads as PNG. Perfect
   * for printing on flyers, posters, table tents, or shop windows.
   */
  const downloadQR = (ev) => {
    const svg = document.getElementById(`qr-event-${ev.id}`);
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      canvas.width = 1024;
      canvas.height = 1280;
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, 1024, 1280);
      ctx.drawImage(img, 112, 80, 800, 800);
      ctx.fillStyle = '#111114';
      ctx.font = 'bold 44px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(ev.name, 512, 980);
      if (ev.event_date) {
        ctx.font = '28px Arial';
        ctx.fillStyle = '#666';
        ctx.fillText(ev.event_date, 512, 1030);
      }
      ctx.font = 'bold 26px Arial';
      ctx.fillStyle = '#D44E20';
      ctx.fillText('Scansiona per acquistare i biglietti', 512, 1100);
      const link = document.createElement('a');
      link.download = `QR_${ev.name.replace(/\s+/g, '_')}_biglietti.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const load = useCallback(async () => {
    try {
      const r = await adminApi.get('/admin/events');
      const ticketed = (r.data || []).filter(e => e.ticketing_enabled);
      setEvents(ticketed);
    } catch { toast.error('Errore caricamento'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleEvent = async (eventId) => {
    const isOpen = expanded[eventId];
    setExpanded(e => ({ ...e, [eventId]: !isOpen }));
    if (!isOpen && !bookings[eventId]) {
      try {
        const r = await adminApi.get(`/admin/events/${eventId}/bookings`);
        setBookings(b => ({ ...b, [eventId]: r.data.bookings || [] }));
      } catch { toast.error('Errore caricamento prenotazioni'); }
    }
  };

  const openBookingsList = async (ev) => {
    setBookingsList({ event: ev, data: null });
    try {
      const r = await adminApi.get(`/admin/events/${ev.id}/bookings`);
      setBookingsList({ event: ev, data: r.data.bookings || [] });
      // Also refresh the inline cache so the expanded view stays in sync.
      setBookings(b => ({ ...b, [ev.id]: r.data.bookings || [] }));
    } catch {
      toast.error('Errore caricamento prenotati');
      setBookingsList(null);
    }
  };

  const handleResetEvent = async (ev) => {
    const confirmMsg = `⚠️ Sei sicuro di voler ELIMINARE TUTTI i biglietti, ordini e pagamenti dell'evento "${ev.name}"?\n\nLa configurazione dell'evento (fasce, prezzi, date) viene preservata.\n\nQuesta azione è IRREVERSIBILE e va usata SOLO per pulire i dati di test prima di andare live.\n\nDigita "RESET" per confermare:`;
    // eslint-disable-next-line no-alert
    const answer = window.prompt(confirmMsg, '');
    if (answer !== 'RESET') {
      if (answer !== null) toast.error('Reset annullato (devi digitare RESET).');
      return;
    }
    setResetting(ev.id);
    try {
      const r = await adminApi.post(`/admin/events/${ev.id}/reset-data`);
      toast.success(`Reset completato: ${r.data.tickets_deleted} biglietti e ${r.data.orders_deleted} ordini eliminati.`);
      setBookings(b => ({ ...b, [ev.id]: [] }));
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore reset evento');
    } finally {
      setResetting(null);
    }
  };

  if (loading) return (
    <div className="p-8 flex items-center justify-center h-64">
      <div className="w-8 h-8 border-3 border-[#D44E20] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="admin-events-tickets-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#1A1A1A]">Biglietti Eventi</h1>
          <p className="text-[#64748B] text-sm mt-0.5">Gestisci le prenotazioni e i biglietti degli eventi in vendita</p>
        </div>
        <button onClick={load} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><RefreshCw size={18} /></button>
      </div>

      {events.length === 0 && (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center">
          <Ticket size={32} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500 text-sm">Nessun evento con biglietteria attiva</p>
          <p className="text-xs text-slate-400 mt-1">Vai su Engagement &gt; Eventi e attiva la biglietteria su un evento</p>
        </div>
      )}

      <div className="space-y-3">
        {events.map(ev => {
          const isOpen = expanded[ev.id];
          const evBookings = bookings[ev.id] || [];
          return (
            <div key={ev.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={`event-tickets-${ev.id}`}>
              {/* Header row: stack vertically on mobile so the 3 action
                  buttons (Live / Link / Reset) don't overlap each other
                  and don't squeeze the event name + meta line. */}
              <div className="w-full px-5 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <button onClick={() => toggleEvent(ev.id)} className="flex items-center gap-3 text-left flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center flex-shrink-0">
                    <CalendarDays size={18} className="text-amber-600" />
                  </div>
                  <div className="min-w-0">
                    <span className="font-semibold text-[#1A1A1A]">{ev.name}</span>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5">
                      {ev.event_date && <span className="text-[10px] text-slate-500">{ev.event_date}</span>}
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); openBookingsList(ev); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); openBookingsList(ev); } }}
                        data-testid={`event-bookings-count-${ev.id}`}
                        title="Vedi elenco prenotati con dati di contatto"
                        className="inline-flex items-center gap-1 text-[10px] text-[#D44E20] font-semibold hover:underline cursor-pointer"
                      >
                        <Users size={10} />
                        {ev.total_booked || 0} prenotati
                      </span>
                      <span className="text-[10px] text-slate-400">{ev.ticket_tiers?.length || 0} fasce</span>
                    </div>
                  </div>
                  <span className="sm:hidden ml-auto flex-shrink-0 text-slate-400">
                    {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </span>
                </button>
                {/* Action buttons: wrap on mobile, inline on desktop. */}
                <div className="flex flex-wrap items-center gap-2 sm:flex-shrink-0">
                  <a href={`/admin/events/${ev.id}/live`} target="_blank" rel="noopener noreferrer"
                    data-testid={`event-live-btn-${ev.id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-bold transition-colors">
                    <Activity size={12} />
                    Live
                    <ExternalLink size={10} />
                  </a>
                  {ev.slug && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const url = `${window.location.origin}/event/${ev.slug}/checkout`;
                        navigator.clipboard.writeText(url);
                        toast.success(`Link copiato: ${url}`);
                      }}
                      data-testid={`event-copy-link-${ev.id}`}
                      title={`${window.location.origin}/event/${ev.slug}/checkout`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 text-xs font-bold transition-colors"
                    >
                      <Link2 size={12} />
                      Link
                    </button>
                  )}
                  {/* Reset event data — wipe all bookings/tickets/payments to clean up after testing */}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleResetEvent(ev); }}
                    disabled={resetting === ev.id}
                    data-testid={`event-reset-data-btn-${ev.id}`}
                    title="Elimina tutti i biglietti e ordini di questo evento (per testing)"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 text-xs font-bold transition-colors disabled:opacity-50"
                  >
                    <Trash2 size={12} />
                    {resetting === ev.id ? 'Reset…' : 'Reset dati'}
                  </button>
                  <button onClick={() => toggleEvent(ev.id)} className="hidden sm:inline-flex flex-shrink-0 text-slate-400 p-1">
                    {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>
                </div>
              </div>

              {isOpen && (
                <div className="border-t border-slate-100 px-5 py-4 bg-slate-50/50">
                  {/* Public checkout URL + QR */}
                  {ev.slug && (() => {
                    const url = `${window.location.origin}/event/${ev.slug}/checkout`;
                    return (
                      <div className="bg-white rounded-xl border border-emerald-200 p-4 mb-4" data-testid={`event-checkout-url-expanded-${ev.id}`}>
                        <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider flex items-center gap-1.5 mb-3">
                          <Link2 size={11} /> Link pubblico checkout — dedicato a questo evento
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                          {/* QR code */}
                          <div className="flex flex-col items-center gap-2 bg-emerald-50/40 border border-emerald-100 rounded-xl p-3">
                            <QRCodeSVG id={`qr-event-${ev.id}`} value={url} size={140} level="H" />
                            <button
                              type="button"
                              onClick={() => downloadQR(ev)}
                              data-testid={`download-qr-${ev.id}`}
                              className="text-[10px] bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-3 py-1.5 rounded flex items-center gap-1.5"
                            >
                              <Download size={11} /> Scarica QR (PNG)
                            </button>
                            <p className="text-[9px] text-slate-500 text-center leading-tight">Stampa su volantini, locandine,<br/>menu, vetrine del locale.</p>
                          </div>

                          {/* URL + actions */}
                          <div className="md:col-span-2 space-y-2">
                            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">URL</p>
                            <div className="flex items-center gap-2 bg-emerald-50/50 border border-emerald-100 rounded-lg px-3 py-2">
                              <span className="font-mono text-[11px] text-slate-700 flex-1 break-all">{url}</span>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                              <button
                                type="button"
                                onClick={() => { navigator.clipboard.writeText(url); toast.success('Link copiato'); }}
                                className="flex-shrink-0 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-3 py-1.5 rounded flex items-center gap-1.5"
                              >
                                <Copy size={11} /> COPIA link
                              </button>
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-shrink-0 text-[11px] bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-bold px-3 py-1.5 rounded flex items-center gap-1.5"
                              >
                                <ExternalLink size={11} /> APRI
                              </a>
                            </div>
                            <p className="text-[10px] text-slate-500 leading-relaxed mt-2">
                              Questo URL apre <b>solo questo evento</b> ({ev.name}) con le sue fasce di prezzo. Incollalo nelle ad Meta/Google, newsletter, post Instagram, landing page o stampa il QR per usarlo offline.
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Tiers summary */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {ev.ticket_tiers?.map((tier, i) => (
                      <div key={i} className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs">
                        <span className="font-semibold text-[#1A1A1A]">{tier.name}</span>
                        <span className="text-slate-400 ml-2">{tier.price > 0 ? `${tier.price.toFixed(2)} EUR` : 'Gratis'}</span>
                        <span className="text-[#D44E20] ml-2 font-semibold">{tier.booked || 0}/{tier.capacity || '∞'}</span>
                      </div>
                    ))}
                  </div>

                  {/* Bookings list */}
                  {evBookings.length === 0 ? (
                    <p className="text-xs text-slate-400 italic">Nessuna prenotazione ancora</p>
                  ) : (
                    <div className="space-y-1">
                      <div className="grid grid-cols-5 gap-2 text-[10px] text-slate-400 font-semibold px-2 pb-1">
                        <span>Cliente</span><span>Fascia</span><span>Stato</span><span>Data</span><span>Telefono</span>
                      </div>
                      {evBookings.map(b => {
                        const st = TICKET_STATUS[b.status] || TICKET_STATUS.pending;
                        return (
                          <div key={b.id} className="grid grid-cols-5 gap-2 px-2 py-2 bg-white rounded-lg border border-slate-100 text-xs items-center">
                            <span className="text-[#1A1A1A] font-medium truncate">{b.customer_name || 'Ospite'}</span>
                            <span className="text-slate-500">{b.tier_name}</span>
                            <span className="flex items-center gap-1" style={{ color: st.color }}>
                              {b.status === 'valid' && <CheckCircle size={10} />}
                              {b.status === 'checked_in' && <CheckCircle size={10} />}
                              {b.status === 'pending' && <Clock size={10} />}
                              {b.status === 'cancelled' && <XCircle size={10} />}
                              {st.label}
                            </span>
                            <span className="text-slate-400">{b.created_at ? new Date(b.created_at).toLocaleDateString('it-IT') : ''}</span>
                            <span className="text-slate-400">{b.customer_phone || '-'}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {bookingsList && (
        <BookingsListModal
          event={bookingsList.event}
          bookings={bookingsList.data}
          onClose={() => setBookingsList(null)}
        />
      )}
    </div>
  );
}


/**
 * Modal listing all bookings for an event with the buyer's full contact
 * info (name, phone, email) so the admin can reach attendees out-of-band.
 * Aggregates tickets by buyer to avoid showing N rows for a 4-tickets order.
 */
function BookingsListModal({ event, bookings, onClose }) {
  // Aggregate per buyer using email-or-phone as a soft key.
  const grouped = React.useMemo(() => {
    if (!bookings) return null;
    const map = new Map();
    for (const t of bookings) {
      const key = (t.buyer_email || t.buyer_phone || t.customer_id || `t-${t.id}`).toLowerCase();
      if (!map.has(key)) {
        map.set(key, {
          first_name: t.buyer_first_name || '',
          last_name: t.buyer_last_name || '',
          phone: t.buyer_phone || t.customer_phone || '',
          email: t.buyer_email || '',
          tickets: [],
        });
      }
      map.get(key).tickets.push(t);
    }
    return Array.from(map.values()).sort((a, b) => b.tickets.length - a.tickets.length);
  }, [bookings]);

  const exportCsv = () => {
    if (!grouped) return;
    const rows = [['Nome', 'Cognome', 'Telefono', 'Email', 'Numero biglietti', 'Fasce']];
    for (const g of grouped) {
      const breakdown = g.tickets.reduce((acc, t) => {
        acc[t.tier_name] = (acc[t.tier_name] || 0) + 1;
        return acc;
      }, {});
      const tiersStr = Object.entries(breakdown).map(([k, v]) => `${v}× ${k}`).join('; ');
      rows.push([g.first_name, g.last_name, g.phone, g.email, g.tickets.length, tiersStr]);
    }
    const csv = rows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `prenotati-${(event.name || 'evento').toLowerCase().replace(/[^a-z0-9]+/g, '-')}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="bookings-list-modal">
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <header className="px-5 py-3 border-b border-slate-200 flex items-center gap-3">
          <Users size={18} className="text-[#D44E20]" />
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-[#1A1A1A] truncate">Prenotati — {event.name}</h2>
            <p className="text-xs text-[#64748B]">
              {grouped == null ? 'Caricamento…' : `${grouped.length} acquirent${grouped.length === 1 ? 'e' : 'i'} · ${bookings?.length || 0} biglietti totali`}
            </p>
          </div>
          {grouped && grouped.length > 0 && (
            <button onClick={exportCsv} data-testid="export-bookings-csv-btn" className="text-[11px] font-bold text-[#D44E20] hover:underline flex-shrink-0">
              Esporta CSV
            </button>
          )}
          <button onClick={onClose} data-testid="bookings-list-close-btn" className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-[#64748B]">
            <X size={16} />
          </button>
        </header>

        <main className="flex-1 overflow-y-auto px-5 py-4">
          {grouped == null && (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {grouped && grouped.length === 0 && (
            <p className="text-sm text-[#64748B] text-center py-8" data-testid="no-bookings-msg">
              Nessun prenotato per questo evento.
            </p>
          )}
          {grouped && grouped.length > 0 && (
            <ul className="divide-y divide-slate-100">
              {grouped.map((g, i) => {
                const breakdown = g.tickets.reduce((acc, t) => {
                  acc[t.tier_name] = (acc[t.tier_name] || 0) + 1;
                  return acc;
                }, {});
                return (
                  <li key={i} className="py-3" data-testid={`booking-row-${i}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-[#1A1A1A]">
                          {(g.first_name + ' ' + g.last_name).trim() || 'Ospite'}
                        </p>
                        <div className="flex flex-wrap items-center gap-3 mt-1 text-[11px] text-[#64748B]">
                          {g.phone && (
                            <a href={`tel:${g.phone}`} className="flex items-center gap-1 hover:text-[#D44E20]">
                              <Phone size={10} /> {g.phone}
                            </a>
                          )}
                          {g.email && (
                            <a href={`mailto:${g.email}`} className="flex items-center gap-1 hover:text-[#D44E20] truncate max-w-[200px]">
                              <Mail size={10} /> {g.email}
                            </a>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {Object.entries(breakdown).map(([tier, qty]) => (
                            <span key={tier} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-50 text-amber-800 text-[10px] font-semibold">
                              {qty}× {tier}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className="text-base font-black text-[#D44E20] tabular-nums">{g.tickets.length}</span>
                        <p className="text-[9px] text-[#94A3B8] uppercase tracking-wider">biglietti</p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </main>
      </div>
    </div>
  );
}
